// Bê từ finiva-admin src/shared/layouts/MainLayout.tsx. Sửa: menu của admin; bỏ tên tập đoàn, ô cân đối, chuông thông báo
import { useState } from 'react'
import { NavLink, Link, Outlet } from 'react-router-dom'
import { LogOut, Settings } from 'lucide-react'
import { ErrorBoundary } from '@/shared/components/ui/error-boundary'
import { BrandLockup } from '@shared/components/ui/brand'
import { ConfirmDialog } from '@shared/components/ui/confirm-dialog'
import { ScrollArea } from '@shared/components/ui/scroll-area'
import { ROUTES } from '@config/routes'
import { accountLabel, useCurrentUser, useLogout } from '@modules/auth'
import { PreferencesGate } from '@modules/settings'
import { useProfile } from '@modules/profile'
import { Crumbs } from './Crumbs'
import { NavSearch } from './NavSearch'
import { canSeeNavItem, NAV_GROUPS } from './nav'

// admin: menu của trạm quản trị Finiva ở nav.ts (dùng chung với breadcrumb + tìm chức năng)
export function MainLayout() {
  const { user } = useCurrentUser()
  const { logout } = useLogout()
  const { data: profile } = useProfile()
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)

  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ')
  const displayName = fullName || user?.email || 'A'
  const initial = displayName[0].toUpperCase()

  return (
    <div className="flex h-screen overflow-hidden bg-secondary">
      {/* ── Sidebar ── */}
      <aside className="hidden w-sidebar shrink-0 flex-col bg-sidebar text-sidebar-foreground lg:flex">
        {/* Logo */}
        <div className="flex h-topbar items-center border-b border-sidebar-border px-5">
          <BrandLockup subtitle="Trạm quản trị" />
        </div>

        {/* Nav */}
        <ScrollArea className="min-h-0 flex-1" thumbClassName="bg-sidebar-foreground/25">
          <nav className="space-y-4 px-3 py-4">
            {NAV_GROUPS.map((group) => {
              const visibleItems = group.items.filter((item) => canSeeNavItem(item, user))
              if (visibleItems.length === 0) return null
              return (
                <div key={group.label}>
                  <p className="mb-1 px-2 text-2xs font-semibold uppercase tracking-widest text-sidebar-muted">
                    {group.label}
                  </p>
                  <div className="space-y-0.5">
                    {visibleItems.map(({ to, label, icon: Icon }) => (
                      <NavLink
                        key={to}
                        to={to}
                        // /admin là tiền tố của /admin/roles — phải khớp đúng, không thì "Quản trị viên" sáng cùng lúc
                        end={to === ROUTES.ADMIN}
                        className={({ isActive }) =>
                          `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                            isActive
                              ? 'bg-sidebar-accent font-medium text-sidebar-foreground'
                              : 'text-sidebar-muted hover:bg-sidebar-accent/60 hover:text-sidebar-foreground'
                          }`
                        }
                      >
                        <Icon className="size-4 shrink-0" />
                        {label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              )
            })}
            {/* Trang Cài đặt (finiva-admin 02/10/2026): ai đăng nhập cũng có — nhóm Của tôi không cần quyền */}
            <div className="border-t border-sidebar-border pt-3">
              <NavLink
                to={ROUTES.SETTINGS}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? 'bg-sidebar-accent font-medium text-sidebar-foreground'
                      : 'text-sidebar-muted hover:bg-sidebar-accent/60 hover:text-sidebar-foreground'
                  }`
                }
              >
                <Settings className="size-4 shrink-0" />
                Cài đặt
              </NavLink>
            </div>
          </nav>
        </ScrollArea>

        {/* User */}
        <div className="border-t border-sidebar-border p-4">
          <div className="flex items-center gap-3">
            <Link
              to="/settings/account"
              title="Cài đặt tài khoản"
              className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-1 py-1 transition-colors hover:bg-sidebar-accent/60"
            >
              {profile?.avatarUrl ? (
                <img
                  src={profile.avatarUrl}
                  alt="avatar"
                  className="h-8 w-8 shrink-0 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-xs font-semibold text-sidebar-foreground">
                  {initial}
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate text-xs font-medium text-sidebar-foreground">
                  {fullName || user?.email}
                </p>
                {/* vai trò chính (finiva-admin 01/10) */}
                <p className="truncate text-2xs text-sidebar-muted">{accountLabel(user)}</p>
              </div>
            </Link>
            <button
              onClick={() => setShowLogoutConfirm(true)}
              title="Đăng xuất"
              className="shrink-0 text-sidebar-muted transition-colors hover:text-sidebar-foreground"
            >
              <LogOut className="size-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Content ── */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        {/* thanh đầu: đường dẫn trang (trái) + tìm chức năng (giữa) – cùng bố cục customer/web */}
        <header className="grid h-topbar shrink-0 grid-cols-[minmax(0,1fr)_minmax(240px,440px)] items-center gap-3 border-b bg-card px-4 sm:px-5 lg:grid-cols-[minmax(0,1fr)_minmax(280px,440px)_minmax(0,1fr)]">
          <Crumbs />
          <NavSearch />
        </header>
        {/* relative: phần tử ẩn position:absolute của Radix (select / checkbox) lấy main làm khối chứa → bị cắt trong khung, không làm cả trang cuộn.
            Cuộn bằng ScrollArea (thanh cuộn mảnh); [&>div]:!block: nội dung co theo bề rộng khung */}
        <main className="relative min-h-0 flex-1 overflow-hidden">
          <ScrollArea className="h-full" scrollbars="both" viewportClassName="[&>div]:!block">
            <ErrorBoundary>
              {/* Cài đặt › Hiển thị & mặc định: bảng đọc mật độ / số dòng của tài khoản */}
              <PreferencesGate>
                <Outlet />
              </PreferencesGate>
            </ErrorBoundary>
          </ScrollArea>
        </main>
      </div>

      <ConfirmDialog
        open={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={() => {
          void logout()
        }}
        title="Đăng xuất?"
        description="Bạn có chắc muốn đăng xuất khỏi hệ thống không?"
        confirmLabel="Đăng xuất"
        cancelLabel="Huỷ"
        variant="warning"
      />
    </div>
  )
}
